import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import ErrorState from '../components/ErrorState.jsx'
import InlineAlert from '../components/InlineAlert.jsx'
import { ApiError, api } from '../lib/api.js'
import { useAuth } from '../context/AuthContext.jsx'
import { reasonLabel } from '../lib/reportReasons.js'

/**
 * `/admin/reports` — the moderation queue (MOD-001, PLAN §7, §17.3).
 *
 * Sized to one person, because in the alpha one person is the whole team.
 * Three actions: hide, dismiss, note. Suspending an account is not offered —
 * a half-built version of it, a status column nothing else checks, would be
 * worse than not having one.
 *
 * The route is admin-only on the server; this component's own check exists so
 * a non-admin who types the URL gets a sentence rather than a wall of 403s.
 */
function ReportRow({ report, onResolve }) {
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function act(action) {
    setBusy(true)
    setError('')
    try {
      await onResolve(report, action, note)
    } catch (err) {
      // The row stays, the note stays, and the three buttons come back — the
      // report is still open, which is what the queue should keep showing.
      setError(err instanceof ApiError ? err.message : 'Could not resolve that report just now.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <li className="report">
      <div className="report-head">
        <span className={`chip reason-${report.reason}`}>{reasonLabel(report.reason)}</span>
        <span className="footnote">
          {report.target_type} · reported by {report.reporter || 'a deleted account'} ·{' '}
          {new Date(report.created_at).toLocaleString()}
        </span>
      </div>

      <blockquote className="report-subject">
        <p className="footnote mb-0">
          {report.subject.author ? `@${report.subject.author}` : 'unknown author'}
          {report.subject.status ? ` · ${report.subject.status}` : ''}
        </p>
        <p className="mb-0">{report.subject.excerpt || '(nothing to show)'}</p>
      </blockquote>

      {report.note ? <p className="report-note">“{report.note}”</p> : null}

      <div className="report-actions">
        <label className="sr-only" htmlFor={`note-${report.public_id}`}>
          Resolution note
        </label>
        <input
          id={`note-${report.public_id}`}
          className="input"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Why (goes in the audit log)"
          maxLength={2000}
        />
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => act('hide')}
          disabled={busy}
        >
          Hide
        </button>
        <button
          type="button"
          className="btn btn-quiet btn-sm"
          onClick={() => act('dismiss')}
          disabled={busy}
        >
          Dismiss
        </button>
        <button
          type="button"
          className="btn btn-quiet btn-sm"
          onClick={() => act('note')}
          disabled={busy}
        >
          Note only
        </button>
      </div>

      <InlineAlert>{error}</InlineAlert>
    </li>
  )
}

export default function AdminReports() {
  const { user } = useAuth()
  const [state, setState] = useState({ loading: true, reports: [], counts: {}, actions: [] })
  const [ops, setOps] = useState(null)
  const [error, setError] = useState('')

  const load = useCallback(async (signal) => {
    try {
      const [queue, dashboard] = await Promise.all([
        api.adminReports('open', { signal }),
        api.adminOps({ signal }),
      ])
      setState({ loading: false, ...queue })
      setOps(dashboard)
    } catch (err) {
      if (err.name === 'AbortError') return
      setError(err instanceof ApiError ? err.message : 'The queue did not load.')
      setState((current) => ({ ...current, loading: false }))
    }
  }, [])

  useEffect(() => {
    document.title = 'Reports — Sonologue'
    if (!user?.is_admin) return undefined
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load, user?.is_admin])

  if (!user?.is_admin) {
    return (
      <div className="card card-pad">
        <div className="empty-state">
          <h2>Not your queue</h2>
          <p>Moderation is owner-only in the alpha.</p>
          <Link className="btn btn-tonal" to="/">
            Back to your feed
          </Link>
        </div>
      </div>
    )
  }

  async function resolve(report, action, note) {
    await api.resolveReport(report.public_id, { action, note })
    await load()
  }

  return (
    <>
      <div className="section-header">
        <h1 className="title-2">Reports</h1>
        <span className="footnote">
          {state.counts.open || 0} open · {state.counts.resolved || 0} resolved ·{' '}
          {state.counts.dismissed || 0} dismissed
        </span>
      </div>

      {ops ? (
        <div className="card card-pad ops-grid fade-up">
          <div className="ops-stat">
            <span className="ops-value">{ops.users}</span>
            <span className="footnote">accounts</span>
          </div>
          <div className="ops-stat">
            <span className="ops-value">{ops.ratings}</span>
            <span className="footnote">ratings</span>
          </div>
          <div className="ops-stat">
            <span className="ops-value">{ops.reviews}</span>
            <span className="footnote">reviews</span>
          </div>
          <div className="ops-stat">
            <span className="ops-value">{ops.lists}</span>
            <span className="footnote">lists</span>
          </div>
          <div className={`ops-stat ${ops.jobs.dead ? 'bad' : ''}`}>
            <span className="ops-value">{ops.jobs.dead}</span>
            {/* The one number worth a panel: a stopped queue is invisible from
                every other screen, and "the feed is empty" is what somebody
                would report instead. */}
            <span className="footnote">dead-lettered jobs</span>
          </div>
          <div className="ops-stat">
            <span className="ops-value">{ops.jobs.queued}</span>
            <span className="footnote">queued</span>
          </div>
        </div>
      ) : null}

      <div className="card">
        {error ? (
          <ErrorState title="The queue did not load" message={error} onRetry={() => load()} />
        ) : state.loading ? (
          <div className="card-pad">
            <p className="footnote mb-0">Loading…</p>
          </div>
        ) : state.reports.length === 0 ? (
          <div className="empty-state">
            <h2>Nothing to look at</h2>
            <p>Reports land here as people file them.</p>
          </div>
        ) : (
          <ul className="report-list">
            {state.reports.map((report) => (
              <ReportRow key={report.public_id} report={report} onResolve={resolve} />
            ))}
          </ul>
        )}
      </div>

      {state.actions?.length ? (
        <div className="card card-pad">
          <h2 className="title-3">Recent actions</h2>
          <ul className="action-log">
            {state.actions.map((action, index) => (
              <li key={`${action.created_at}-${index}`} className="footnote">
                <strong>{action.action}</strong> {action.target_type} #{action.target_id}
                {action.reason ? ` — ${action.reason}` : ''} ·{' '}
                {new Date(action.created_at).toLocaleString()}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  )
}
